import { themeAtom } from "../../shared/atoms";
import { Bq5CargoArrangementsM } from "../models/bq5-cargo-arrangements.model";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";
import { twMerge } from "tailwind-merge";

export function Bq5Ballast(
  form: Bq5CargoArrangementsM,
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Ballast</h2>

      {/* Use calculated total for ballast? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Use calculated total for ballast capacities (if no, then enter manual total below).:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="useCalculatedBallast"
                value="yes"
                checked={form.useCalculatedBallast === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="useCalculatedBallast"
                value="no"
                checked={form.useCalculatedBallast === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
          </div>
        </div>
      </div>

      {/* Calculated total for ballast capacities */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Calculated total for ballast capacities</label>
          <input
            disabled={readonly}
            type="text"
            name="calcTotalBallast"
            value={form.calcTotalBallast}
            onChange={handleChange}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      {/* Total Ballast Tank at 100% full */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Total Ballast Tank at 100% full</label>
          <InputCell
            disabled={readonly}
            name="totalBallastTankFull"
            value={form.totalBallastTankFull}
            onChange={handleChange}
            trailingText="Cu. Metres"
            inputType="number"
          />
        </div>
      </div>

      {/* Capacity of ballast holds */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Capacity of ballast holds</label>
          <p className="text-xs font-medium">(specify cargo hold number and capacity)</p>
          <textarea
            disabled={readonly}
            name="ballastHoldsCapacity"
            rows={2}
            value={form.ballastHoldsCapacity}
            onChange={handleChange}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Time required to ballast after discharge</label>
          <InputCell
            disabled={readonly}
            inputType="number"
            name="timeReqBallast"
            value={form.timeReqBallast}
            onChange={handleChange}
            trailingText="Hour"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("timeReqBallastComments")}
            >
              Comments
            </button>
          </label>
          {(form.timeReqBallastComments || isCommentsVisible("timeReqBallastComments")) && (
            <textarea
              disabled={readonly}
              name="timeReqBallastComments"
              value={form.timeReqBallastComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        <div className="col-span-1">
          <label className="block text-sm font-bold">Time required to deballast prior to loading</label>
          <InputCell
            disabled={readonly}
            inputType="number"
            name="timeReqDeballast"
            value={form.timeReqDeballast}
            onChange={handleChange}
            trailingText="Hour"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("timeReqDeballastComments")}
            >
              Comments
            </button>
          </label>
          {(form.timeReqDeballastComments || isCommentsVisible("timeReqDeballastComments")) && (
            <textarea
              disabled={readonly}
              name="timeReqDeballastComments"
              value={form.timeReqDeballastComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="mt-2 block text-sm font-bold">Max ballasting rate</label>
          <InputCell
            disabled={readonly}
            name="maxBallastingRate"
            value={form.maxBallastingRate}
            onChange={handleChange}
            trailingText="Cu. Metres/Hour"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("maxBallastingRateComments")}
            >
              Comments
            </button>
          </label>
          {(form.maxBallastingRateComments || isCommentsVisible("maxBallastingRateComments")) && (
            <textarea
              disabled={readonly}
              name="maxBallastingRateComments"
              value={form.maxBallastingRateComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        <div className="col-span-1">
          <label className="mt-2 block text-sm font-bold">Max deballasting rate</label>
          <InputCell
            disabled={readonly}
            name="maxDeballastingRate"
            value={form.maxDeballastingRate}
            onChange={handleChange}
            trailingText="Cu. Metres/Hour"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("maxDeballastingRateComments")}
            >
              Comments
            </button>
          </label>
          {(form.maxDeballastingRateComments || isCommentsVisible("maxDeballastingRateComments")) && (
            <textarea
              disabled={readonly}
              name="maxDeballastingRateComments"
              value={form.maxDeballastingRateComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Total unpumpable quantity in the ballast</label>
          <InputCell
            disabled={readonly}
            name="totalUnpumpableQty"
            value={form.totalUnpumpableQty}
            onChange={handleChange}
            trailingText="Cu. Metres"
            inputType="number"
          />
        </div>
      </div>
    </div>
  );
}
